import React, { useState, useEffect } from "react";
import styles from "./styles.module.scss";
import cx from "classnames";
import PropTypes from "prop-types";
import DecimalInput from "../DecimalInput";
import t from "../../../utils/translate";
import { ScreenReaderLabel } from "@ww-digital/web-foundations-react/modules";
import FormErrorMessage from "../../FormErrorMessage";
import isOutOfBounds from "../../../utils/isOutOfBounds";
import useEvent from "../../../utils/useEvent";
import useAppContext from "../../AppContext";
import { capitalize } from "lodash";
import useForceValidate from "../../../utils/useForceValidate";
import { SEQUENCE_POUND_LIMITS } from "../../../utils/weight";

function SingleWeightInput({
  name = "weight-input",
  market,
  label = {
    placeholder: "",
    appended: ""
  },
  value,
  onChange = () => {},
  min,
  max,
  isShortUnit,
  labelClassName = "",
  errorClassName = "",
  fieldsetWrapperClassName = "",
  capitalizeLabel = false
}) {
  const { newQuiz, forceValidation } = useAppContext();

  const [hasError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const isWeightInvalid = weight =>
    weight === null ||
    isOutOfBounds(
      weight,
      newQuiz.isSequence ? SEQUENCE_POUND_LIMITS.min : min,
      newQuiz.isSequence ? SEQUENCE_POUND_LIMITS.max : max
    );

  const getErrorMessage = v => {
    let message = t("WEIGHT_INVALID");
    if (!v) {
      message = t("FIELD_ERROR_REQUIRED");
    } else if (v < SEQUENCE_POUND_LIMITS.min) {
      message = t("JS_WEIGHT_LIMIT_MIN");
    } else if (v > SEQUENCE_POUND_LIMITS.max) {
      message = t("JS_WEIGHT_LIMIT_MAX");
    }

    return message;
  };

  const { hasTriggered } = useForceValidate({
    trigger: forceValidation,
    validate: () => {
      if (!errorMessage) {
        const invalid = isWeightInvalid(value);
        if (invalid) {
          setErrorMessage(getErrorMessage(value));
          setError(true);
        }
      }
    }
  });

  const handleChange = (_, value) => {
    const isInvalid = isWeightInvalid(value);
    if (newQuiz.isSequence) {
      setErrorMessage(getErrorMessage(value));
    }
    setError(isInvalid);
    onChange(isInvalid ? null : value);
  };

  const { listen } = useEvent("SettingsValidation");
  useEffect(() => {
    return listen(() => {
      setError(value === null || isOutOfBounds(value, min, max));
    });
  }, [min, max, listen, value]);

  const renderError = newQuiz.isSequence ? hasError && hasTriggered : hasError;

  return (
    <>
      <div
        className={cx(
          styles.singleInputContainer,
          styles.appendedLabel,
          fieldsetWrapperClassName
        )}
      >
        <div className={styles.wrapper}>
          <DecimalInput
            id={name}
            key={label.appended}
            aria-required={true}
            market={market}
            mask="000.0"
            error={hasError}
            defaultValue={value ?? ""}
            onChange={handleChange}
            tabIndex={1}
            autoFocus={true}
            placeholder={newQuiz.isSequence ? t("JS_WEIGHT_PLACEHOLDER") : ""}
          />
          {newQuiz.isSequence && (
            <div className={styles.suffix}>{t("JS_POUNDS")}</div>
          )}
          <label
            className={cx(styles.tailingLabel, labelClassName)}
            htmlFor={name}
          >
            {isShortUnit
              ? label.appendedShort
              : capitalizeLabel
                ? capitalize(label.appended)
                : label.appended}
          </label>
          {hasError && (
            <ScreenReaderLabel role="alert">
              Weight input of {value} must be between {min} and {max}
            </ScreenReaderLabel>
          )}
        </div>
      </div>
      {/* data-error is used to target and hide SingleWeightInput error messages in WeightGoal */}
      {renderError && (
        <div data-error>
          <FormErrorMessage
            role="alert"
            data-test="weight-input-error"
            className={errorClassName}
          >
            {newQuiz.isSequence && errorMessage
              ? errorMessage
              : t("WEIGHT_INVALID")}
          </FormErrorMessage>
        </div>
      )}
    </>
  );
}

SingleWeightInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.shape({
    placeholder: PropTypes.string,
    appended: PropTypes.string
  }),
  min: PropTypes.number,
  max: PropTypes.number,
  market: PropTypes.string.isRequired
};

export default SingleWeightInput;
